import clsx from 'clsx';
import { Button } from '@/components/button';
import { useTranslation } from '@/contexts/i18n-context';
import styles from './vouched-instructions.module.css';

export type VouchedInstructionType = 'surrogate' | 'patient' | 'reverify';

const translationBase = 'visit.vouched.instructions';

interface VouchedInstructionsProps {
  type: VouchedInstructionType;
  onContinue: () => void;
}

export function VouchedInstructions({ type, onContinue }: VouchedInstructionsProps) {
  if (type === 'surrogate') {
    return <SurrogateInstructions onContinue={onContinue} />;
  }

  return <PhotoInstructions variant={type} onContinue={onContinue} />;
}

const surrogateSteps = [
  {
    image: '/vouched/id-photo.svg',
    label: `${translationBase}.surrogate.steps.surrogateId`,
    alt: `${translationBase}.surrogate.surrogateIdAlt`,
  },
  {
    image: '/vouched/surrogate-photo.svg',
    label: `${translationBase}.surrogate.steps.surrogateSelfie`,
    alt: `${translationBase}.surrogate.surrogateSelfieAlt`,
  },
  {
    image: '/vouched/patient-photo.svg',
    label: `${translationBase}.surrogate.steps.patientPhoto`,
    alt: `${translationBase}.surrogate.patientPhotoAlt`,
  },
] as const;

interface SurrogateInstructionsProps {
  onContinue: () => void;
}

function SurrogateInstructions({ onContinue }: SurrogateInstructionsProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={clsx('h3', styles.brandText)}>{t(`${translationBase}.surrogate.title`)}</h1>
      </div>
      <div className={styles.stepsCard}>
        <h2 className={clsx('h4', styles.brandText, styles.centered)}>
          {t(`${translationBase}.surrogate.whatYouNeed`)}
        </h2>
        <div className={styles.steps}>
          {surrogateSteps.map((step, index) => (
            <div key={step.label} className={styles.step}>
              <span className={styles.stepNumber}>{index + 1}</span>
              <div className={styles.stepContent}>
                <img src={step.image} alt={t(step.alt)} className={styles.stepIllustration} />
                <p className={clsx(styles.mutedText, styles.centered)}>{t(step.label)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Button className={styles.continueButton} fullWidth onClick={onContinue}>
        {t(`${translationBase}.surrogate.continueButton`)}
      </Button>
    </div>
  );
}

interface PhotoInstructionsProps {
  variant: Exclude<VouchedInstructionType, 'surrogate'>;
  onContinue: () => void;
}

function PhotoInstructions({ variant, onContinue }: PhotoInstructionsProps) {
  const { t } = useTranslation();
  const base = `${translationBase}.${variant}` as const;

  return (
    <div className={styles.container}>
      <p className={clsx('bold', styles.tagline)}>{t(`${base}.tagline`)}</p>
      <div className={styles.photoInstructions}>
        <img
          src="/vouched/patient-photo.svg"
          alt={t(`${base}.illustrationAlt`)}
          className={styles.illustration}
        />
        <p className="body-1">{t(`${base}.description`)}</p>
      </div>
      <Button className={styles.continueButton} fullWidth onClick={onContinue}>
        {t(`${base}.continueButton`)}
      </Button>
    </div>
  );
}
